import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import MyLoader from '../Spinner/MyLoader';
import SessionManager from '../Utils/SessionManager';
import { Redirect, Route, BrowserRouter as Router } from 'react-router-dom';
import '../css/Admin_css/admin-customize.css';
import myToast from '../Utils/MyToast';
import MdlDeleteExamConfirmation from '../dialog/MdlDeleteExamConfirmation';
import MdlSelectSession from '../dialog/MdlSelectSession';
import BackButton from '../ChildComponent/BackButton';
import COLORS from '../resources/colors/COLORS';
import { FaTrashAlt, FaSync, FaCalendarAlt, FaClipboardList } from 'react-icons/fa';

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const DeleteExam = () => {
  const token = localStorage.getItem("token");
  const [loggedIn] = useState(!!token);
  const [Loading, setLoading] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [EXAM_ID, setEXAM_ID] = useState('');
  const [SESSION_ID, setSESSION_ID] = useState('');
  const [EXAM_NAME, setEXAM_NAME] = useState('');
  const [examList, setExamList] = useState([]);
  const [examListLoading, setExamListLoading] = useState(false);
  const [isOpenSessionDialog, setIsOpenSessionDialog] = useState(false);
  const [SESSION_NAME, setSESSION_NAME] = useState('');
  const [SESSION_STATUS, setSESSION_STATUS] = useState('');
  const sortedExams = useMemo(() => {
    const typeOrder = { EXAM: 0, CLASS_TEST: 1 };
    return [...examList].sort((a, b) => {
      const ta = typeOrder[String(a.exam_type || '').toUpperCase()] ?? 2;
      const tb = typeOrder[String(b.exam_type || '').toUpperCase()] ?? 2;
      return ta - tb;
    });
  }, [examList]);

  const filteredExams = sortedExams;

  const groupedExams = useMemo(() => {
    const groups = [];
    let lastType = null;
    filteredExams.forEach(exam => {
      const t = String(exam.exam_type || 'OTHER').toUpperCase();
      if (t !== lastType) {
        groups.push({ type: t, items: [] });
        lastType = t;
      }
      groups[groups.length - 1].items.push(exam);
    });
    return groups;
  }, [filteredExams]);

  const DeleteExamConfirm = useCallback(() => {
    setShowDeleteConfirmation(prev => !prev);
  }, []);

  const fetchExamList = useCallback((sessionIdParam) => {
    setExamListLoading(true);
    const URL = process.env.REACT_APP_SERVER_NAME + process.env.REACT_APP_TARGET_DIR + 'getExamList.php';
    const sessionId = sessionIdParam || SessionManager.getCurrentSession()?.[0]?.session_id || '';
    axios
      .get(URL, { params: { session_id: sessionId, school_code: SessionManager.getSchoolCode() } })
      .then(res => res.data)
      .then(response => {
        setExamListLoading(false);
        if (response.api_status === 'OK') {
          setExamList(response.data || []);
        } else {
          myToast.notify_warning(response.api_remark || 'Unable to fetch exam list');
        }
      })
      .catch(() => { setExamListLoading(false); });
  }, []);

  useEffect(() => {
    const mSession = SessionManager.getCurrentSession();
    const sid = mSession?.[0]?.session_id || '';
    const sname = mSession?.[0]?.session_name || '';
    const sstatus = mSession?.[0]?.status || '';
    setSESSION_ID(sid);
    setSESSION_NAME(sname);
    setSESSION_STATUS(sstatus);
    fetchExamList(sid);
  }, [fetchExamList]);

  useEffect(() => {
    if (SESSION_ID) fetchExamList(SESSION_ID);
  }, [SESSION_ID, fetchExamList]);

  const HandleConfirmExam = useCallback(() => {
    const URL = process.env.REACT_APP_SERVER_NAME + process.env.REACT_APP_TARGET_DIR + 'DeleteExam.php';
    const SCHOOL_CODE = SessionManager.getSchoolCode();
    const staff_id = String(SessionManager.getStaffDetails()?.staff_id || '');
    setLoading(true);
    axios.get(URL, { params: { staff_id, session_id: SESSION_ID, exam_id: EXAM_ID, school_code: SCHOOL_CODE } })
      .then(res => res.data)
      .then(response => {
        setLoading(false);
        setShowDeleteConfirmation(false);
        if (response.api_status === 'OK') {
          myToast.notify_success(response.api_remark || 'Exam deleted');
          fetchExamList(SESSION_ID);
        } else {
          myToast.notify_warning(response.api_remark || 'Failed to delete exam');
        }
      })
      .catch(() => {
        setLoading(false);
        setShowDeleteConfirmation(false);
        myToast.notify_error('Unable to delete exam');
      });
  }, [EXAM_ID, SESSION_ID, fetchExamList]);

  if (!loggedIn) return <Redirect to='/Login' />;

  return (
    <Router>
      <Route path='/AdminMain/DeleteExam' exact render={() => (
        <div className="content payroll-page sm-page el-page">
          <div className="content-header">
            <div className="leftside-content-header">
              <ul className="breadcrumbs">
                <li><i className="fa fa-home" aria-hidden="true" /><a href="#">Delete Exam</a></li>
              </ul>
            </div>
            <div className="rightside-content-header">
              <BackButton />
            </div>
          </div>

          <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell">
            <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
              <div className="sm-hero__brand">
                <div className="sm-hero__icon"><FaTrashAlt /></div>
                <div className="min-w-0">
                  <h1 className="sm-hero__title">Delete Exam</h1>
                  <p className="sm-hero__sub">Select an exam to permanently remove it and all its dependent records</p>
                </div>
              </div>
              <div className="sm-hero__tools">
                <button
                  className="el-session-pill"
                  onClick={() => setIsOpenSessionDialog(true)}
                  title="Change session"
                >
                  <FaCalendarAlt style={{ fontSize: 12 }} />
                  <span>{SESSION_NAME || 'Select Session'}</span>
                  {SESSION_STATUS && (
                    <span className="el-session-pill__status">{SESSION_STATUS}</span>
                  )}
                </button>
                <button
                  className="pp-icon-btn"
                  title="Refresh"
                  onClick={() => fetchExamList(SESSION_ID)}
                  disabled={examListLoading}
                >
                  <FaSync className={examListLoading ? 'fa-spin' : ''} />
                </button>
              </div>
            </header>

            <div className="el-content">
              {Loading && <MyLoader />}
              {examListLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
                  <MyLoader />
                </div>
              ) : filteredExams.length === 0 ? (
                <div className="sm-empty el-empty">
                  <FaClipboardList style={{ fontSize: 36, color: COLORS.primary_color_shade4, marginBottom: 8 }} />
                  <h3>No exams found</h3>
                  <p>No exams available in this session</p>
                </div>
              ) : (
                <div style={{ padding: '14px 18px' }}>
                  {groupedExams.map(group => (
                    <div key={group.type} style={{
                      marginBottom: 16,
                      border: `1.5px solid ${COLORS.primary_color_shade2}`,
                      borderRadius: 14,
                      background: '#fff',
                      overflow: 'hidden',
                    }}>
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '8px 14px',
                        background: COLORS.primary_color_shade1,
                        borderBottom: `1.5px solid ${COLORS.primary_color_shade2}`,
                      }}>
                        <FaClipboardList style={{ fontSize: 12, color: COLORS.primary_color_shade6 }} />
                        <span style={{ fontSize: 12, fontWeight: 800, color: COLORS.primary_color_shade8, letterSpacing: '.02em' }}>
                          {group.type === 'CLASS_TEST' ? 'Class Tests' : group.type === 'EXAM' ? 'Exams' : group.type}
                        </span>
                        <span style={{
                          marginLeft: 'auto', fontSize: 10, fontWeight: 800,
                          background: COLORS.primary_color_shade2, color: COLORS.primary_color_shade7,
                          borderRadius: 999, padding: '2px 8px',
                        }}>{group.items.length}</span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 5, padding: '10px 12px' }}>
                        {group.items.map((exam, idx) => (
                          <div
                            key={exam.exam_id}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 10,
                              border: '1.5px solid #e8ecf1', borderRadius: 10,
                              padding: '8px 12px', background: '#fff',
                              transition: 'border-color .18s ease, box-shadow .18s ease, background .18s ease',
                            }}
                            onMouseEnter={e => {
                              e.currentTarget.style.borderColor = COLORS.primary_color_shade3;
                              e.currentTarget.style.boxShadow = `0 3px 12px ${COLORS.primary_color_shade1}`;
                              e.currentTarget.style.background = '#fafcff';
                            }}
                            onMouseLeave={e => {
                              e.currentTarget.style.borderColor = '#e8ecf1';
                              e.currentTarget.style.boxShadow = 'none';
                              e.currentTarget.style.background = '#fff';
                            }}
                          >
                            <span style={{
                              width: 26, height: 26, borderRadius: 8, flexShrink: 0,
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: 11, fontWeight: 800, color: COLORS.primary_color_shade7,
                              background: COLORS.primary_color_shade1, border: `1px solid ${COLORS.primary_color_shade2}`,
                            }}>{idx + 1}</span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 800, color: '#1e293b', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.3 }}>
                                {exam.exam_name}
                              </div>
                              {exam.exam_remark && (
                                <div style={{ fontSize: 11, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.2 }}>
                                  {exam.exam_remark}
                                </div>
                              )}
                            </div>
                            <span style={{
                              fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 999,
                              background: COLORS.primary_color_shade1, color: COLORS.primary_color_shade7,
                              border: `1px solid ${COLORS.primary_color_shade2}`,
                              flexShrink: 0, whiteSpace: 'nowrap',
                            }}>
                              {String(exam.participating_class || 0)} classes
                            </span>
                            <button
                              style={{
                                background: '#fff5f5', color: '#e53e3e', border: '1.5px solid #fed7d7',
                                borderRadius: 8, padding: '5px 12px', fontSize: 11, fontWeight: 800,
                                cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5,
                                transition: 'all .18s ease', flexShrink: 0,
                              }}
                              disabled={Loading}
                              onClick={() => {
                                setEXAM_ID(exam.exam_id);
                                setSESSION_ID(exam.session_id);
                                setEXAM_NAME(exam.exam_name);
                                setShowDeleteConfirmation(true);
                              }}
                              onMouseEnter={e => { e.currentTarget.style.background = '#fed7d7'; e.currentTarget.style.borderColor = '#fc8181'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                              onMouseLeave={e => { e.currentTarget.style.background = '#fff5f5'; e.currentTarget.style.borderColor = '#fed7d7'; e.currentTarget.style.transform = 'none'; }}
                            >
                              <FaTrashAlt style={{ fontSize: 10 }} />
                              Delete
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )} />

      {isOpenSessionDialog && (
        <MdlSelectSession
          action={() => setIsOpenSessionDialog(false)}
          action2={(session_id, session_name, session_status) => {
            setSESSION_ID(session_id);
            setSESSION_NAME(session_name);
            setSESSION_STATUS(session_status);
            setIsOpenSessionDialog(false);
            fetchExamList(session_id);
          }}
        />
      )}

      {showDeleteConfirmation && (
        <MdlDeleteExamConfirmation
          actionClose={DeleteExamConfirm}
          actionConfirm={HandleConfirmExam}
          exam_id={EXAM_ID}
          session_id={SESSION_ID}
          exam_name={EXAM_NAME}
        />
      )}
    </Router>
  );
};

export default DeleteExam;
